<script setup lang="ts">
import { ScalarSearchResultItem } from '@scalar/components'
import {
  ScalarIconBracketsCurly,
  ScalarIconTag,
  ScalarIconTerminalWindow,
  ScalarIconTextAlignLeft,
} from '@scalar/icons'
import type { ScalarIconComponent } from '@scalar/icons/types'
import { HttpMethod } from '@scalar/sidebar'
import type { FuseResult } from 'fuse.js'

import type { EntryType, FuseData } from '@/features/Search/types'
import { useI18n } from '@/i18n'

defineProps<{
  id: string
  isSelected: boolean
  result: FuseResult<FuseData>
}>()

const { t } = useI18n()

const ENTRY_ICONS: { [x in EntryType]: ScalarIconComponent } = {
  heading: ScalarIconTextAlignLeft,
  model: ScalarIconBracketsCurly,
  operation: ScalarIconTerminalWindow,
  tag: ScalarIconTag,
  webhook: ScalarIconTerminalWindow,
}

const ENTRY_LABELS = {
  heading: () => t('search.entryTypes.heading'),
  operation: () => t('search.entryTypes.operation'),
  tag: () => t('search.entryTypes.tag'),
  model: () => t('search.entryTypes.model'),
  webhook: () => t('search.entryTypes.webhook'),
} satisfies { [x in EntryType]: () => string }
</script>

<template>
  <ScalarSearchResultItem
    :id="id"
    :icon="ENTRY_ICONS[result.item.type]"
    :selected="isSelected">
    <span
      :class="{
        'text-decoration-line':
          result.item.entry.type === 'operation' &&
          result.item.entry.isDeprecated,
      }">
      <span class="sr-only">
        {{ ENTRY_LABELS[result.item.type]() }}:&nbsp;
        <template
          v-if="
            result.item.entry.type === 'operation' &&
            result.item.entry.isDeprecated
          ">
          ({{ t('search.deprecated') }})&nbsp;
        </template>
      </span>
      {{ result.item.title }}
      <span class="sr-only">,</span>
    </span>
    <template
      v-if="
        result.item.type !== 'webhook' &&
        (result.item.method || result.item.path) &&
        result.item.path !== result.item.title
      "
      #description>
      <span class="inline-flex items-center gap-1">
        <template v-if="result.item.type === 'operation'">
          <HttpMethod
            aria-hidden="true"
            :method="result.item.method ?? 'get'" />
          <span class="sr-only">
            {{ t('search.httpMethod') }} {{ result.item.method ?? 'get' }}
          </span>
        </template>
        <span class="sr-only">{{ t('search.path') }}&nbsp;</span>
        {{ result.item.path }}
      </span>
    </template>
    <template
      v-else-if="result.item.description"
      #description>
      <span class="sr-only">{{ t('search.description') }}&nbsp;</span>
      {{ result.item.description }}
    </template>
  </ScalarSearchResultItem>
</template>
